import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Loader2, RefreshCw, Settings2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { formatDate, cn } from '@/lib/utils'
import { usePIDState } from '@/hooks/use-monitoring'

interface PIDControllerMonitorProps {
  poolID: string
  poolName: string
}

// Reuse helper components from PoolStateMonitor
interface MetricRowProps {
  label: string
  value: string | number
  subdued?: boolean
  icon?: React.ReactNode
}

const MetricRow = ({ label, value, subdued, icon }: MetricRowProps) => (
  <div className={cn(
    "flex items-center justify-between",
    subdued && "text-sm"
  )}>
    <span className={cn(
      "flex items-center gap-1.5",
      subdued ? "text-muted-foreground/80" : "text-muted-foreground"
    )}>
      {icon}
      {label}
    </span>
    <span className={cn(
      "font-medium",
      subdued && "text-muted-foreground"
    )}>{value}</span>
  </div>
)

const SectionDivider = ({ label }: { label: string }) => (
  <div className="border-t pt-3 mt-2">
    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
      {label}
    </p>
  </div>
)

export default function PIDControllerMonitor({ poolID, poolName }: PIDControllerMonitorProps) {
  const [autoRefresh, setAutoRefresh] = useState(false)
  const { toast } = useToast()

  const { data: pidState, isLoading, isFetching, isError, error, refetch } = usePIDState(
    poolID,
    autoRefresh ? 30000 : false
  )

  // Show toast for non-404 errors
  useEffect(() => {
    if (isError) {
      const code = (error as any)?.errorCode
      if (code !== '404' && code !== 'NOT_FOUND') {
        toast({
          title: 'Error',
          description: (error as any)?.message || 'Failed to fetch PID controller state',
          variant: 'destructive',
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError])

  if (!pidState && !isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            PID Controller
          </CardTitle>
          <CardDescription>Configuration and runtime state</CardDescription>
        </CardHeader>
        <CardContent>
          {isError ? (
            <p className="text-sm text-muted-foreground">
              PID controller data not available for this pool
            </p>
          ) : (
            <Button onClick={() => refetch()}>Load PID State</Button>
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              PID Controller
            </CardTitle>
            <CardDescription>Control algorithm tuning and state for {poolName}</CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant={autoRefresh ? 'default' : 'outline'}
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${autoRefresh ? 'animate-spin' : ''}`} />
              {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && !pidState ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : pidState ? (
          <div className="space-y-4">
            {/* Tuning Parameters Section */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                Tuning Parameters
              </p>
              <div className="space-y-2">
                <MetricRow label="P Coefficient" value={pidState.PCoefficient != null ? pidState.PCoefficient.toFixed(4) : 'N/A'} />
                <MetricRow label="I Coefficient" value={pidState.ICoefficient != null ? pidState.ICoefficient.toFixed(4) : 'N/A'} />
                <MetricRow label="D Coefficient" value={pidState.DCoefficient != null ? pidState.DCoefficient.toFixed(4) : 'N/A'} />
              </div>
            </div>

            {/* Control Settings Section */}
            <SectionDivider label="Control Settings" />
            <div className="space-y-2">
              <MetricRow label="Control Band" value={pidState.controlBand != null ? pidState.controlBand.toFixed(4) : 'N/A'} />
              <MetricRow label="Soft Reset Factor" value={pidState.softResetFactor != null ? pidState.softResetFactor.toFixed(2) : '0.0'} />
            </div>

            {/* Last Modified */}
            <div className="pt-2 border-t">
              <p className="text-xs text-muted-foreground">
                Last updated: {pidState.modifiedAt ? formatDate(pidState.modifiedAt) : 'N/A'}
              </p>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}
